import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { InputNumber } from 'primeng/inputnumber';
import { InputText } from 'primeng/inputtext';
import { Checkbox } from 'primeng/checkbox';
import { ColorPicker } from 'primeng/colorpicker';
import { ButtonDirective } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { ContentFilter, ContentFilterData, FilterType } from '../../types/webout';
import { cloneDeep } from '../../utils/clone';
import { humanizeEnum } from '../../utils/format';
import { defaultFilterData, defaultFilterField, isRequiredFilterField, normalizeFilter } from './filter-defaults';

type FieldKind = 'number' | 'color';

interface FilterFieldDef {
	key: keyof ContentFilterData;
	label: string;
	kind: FieldKind;
	/** When the parent filter has dynamic=true, this single value is replaced by a pair: startKey/endKey. */
	dynamicPair?: { startKey: keyof ContentFilterData; endKey: keyof ContentFilterData };
	step?: number;
	tooltip?: string;
}

const COLOR_ALPHA: FilterFieldDef[] = [
	{ key: 'color', label: 'Color', kind: 'color' },
	{
		key: 'alpha',
		label: 'Alpha',
		kind: 'number',
		step: 0.05,
		dynamicPair: { startKey: 'startAlpha', endKey: 'endAlpha' },
	},
];

const FILTER_SCHEMA: Record<string, FilterFieldDef[]> = {
	BLUR: [
		{
			key: 'strength',
			label: 'Strength',
			kind: 'number',
			step: 0.5,
			dynamicPair: { startKey: 'startStrength', endKey: 'endStrength' },
		},
		{
			key: 'quality',
			label: 'Quality',
			kind: 'number',
			step: 1,
			tooltip:
				'Number of render passes for the blur. Higher values give a smoother, more accurate blur at a higher rendering cost. Whole numbers, typically 1–10.',
		},
	],
	ADJUSTMENT: [
		{ key: 'brightness', label: 'Brightness', kind: 'number', step: 0.05 },
		{ key: 'contrast', label: 'Contrast', kind: 'number', step: 0.05 },
		{ key: 'saturation', label: 'Saturation', kind: 'number', step: 0.05 },
		{ key: 'gamma', label: 'Gamma', kind: 'number', step: 0.05 },
		{ key: 'red', label: 'Red', kind: 'number', step: 0.05 },
		{ key: 'green', label: 'Green', kind: 'number', step: 0.05 },
		{ key: 'blue', label: 'Blue', kind: 'number', step: 0.05 },
		{
			key: 'alpha',
			label: 'Alpha',
			kind: 'number',
			step: 0.05,
			dynamicPair: { startKey: 'startAlpha', endKey: 'endAlpha' },
		},
	],
	COLOR_OVERLAY_FILTER: COLOR_ALPHA,
	COLOR_OVERLAY: COLOR_ALPHA,
	DROP_SHADOW: [
		{ key: 'color', label: 'Color', kind: 'color' },
		{ key: 'blur', label: 'Blur', kind: 'number', step: 0.5 },
		{
			key: 'alpha',
			label: 'Alpha',
			kind: 'number',
			step: 0.05,
			dynamicPair: { startKey: 'startAlpha', endKey: 'endAlpha' },
		},
		{ key: 'offsetX', label: 'Offset X', kind: 'number', step: 1 },
		{ key: 'offsetY', label: 'Offset Y', kind: 'number', step: 1 },
	],
	BW: [
		{
			key: 'strength',
			label: 'Strength',
			kind: 'number',
			step: 0.05,
			dynamicPair: { startKey: 'startStrength', endKey: 'endStrength' },
		},
	],
	OLD_FILM_FILTER: [
		{ key: 'sepia', label: 'Sepia', kind: 'number', step: 0.05 },
		{ key: 'noise', label: 'Noise', kind: 'number', step: 0.05 },
		{ key: 'noiseSize', label: 'Noise Size', kind: 'number', step: 0.5 },
		{ key: 'scratch', label: 'Scratch', kind: 'number', step: 0.05 },
		{ key: 'scratchDensity', label: 'Scratch Density', kind: 'number', step: 0.05 },
		{ key: 'scratchWidth', label: 'Scratch Width', kind: 'number', step: 0.5 },
		{ key: 'vignetting', label: 'Vignetting', kind: 'number', step: 0.05 },
		{ key: 'vignettingAlpha', label: 'Vignetting Alpha', kind: 'number', step: 0.05 },
		{ key: 'vignettingBlur', label: 'Vignetting Blur', kind: 'number', step: 0.5 },
	],
};

// `COLOR_OVERLAY` is deliberately absent: the player's `FilterTypes` enum has
// no such member and its `addFilters` switch has no `default:` arm, so choosing
// it produced a filter that saved cleanly and was then silently dropped at
// render time. Legacy data keeps working — `canonicalFilterType` aliases it to
// `COLOR_OVERLAY_FILTER` on read (DEV-757).
const FILTER_TYPE_OPTIONS: { label: string; value: FilterType }[] = (
	['BLUR', 'ADJUSTMENT', 'COLOR_OVERLAY_FILTER', 'DROP_SHADOW', 'BW', 'OLD_FILM_FILTER'] as FilterType[]
).map(value => ({ label: humanizeEnum(value), value }));

interface RenderedField {
	label: string;
	dataKey: keyof ContentFilterData;
	kind: FieldKind;
	step?: number;
	tooltip?: string;
}

interface RenderedFilter {
	index: number;
	filter: ContentFilter;
	type: string;
	dynamic: boolean;
	fields: RenderedField[];
}

@Component({
	selector: 'app-filters-editor',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [FormsModule, Select, InputNumber, InputText, Checkbox, ColorPicker, ButtonDirective, Tooltip],
	templateUrl: './filters-editor.html',
	styleUrl: './filters-editor.scss',
})
export class FiltersEditorComponent {
	filters = input<ContentFilter[]>([]);
	filtersChange = output<ContentFilter[]>();

	protected readonly typeOptions = FILTER_TYPE_OPTIONS;
	protected newType = signal<FilterType>('BLUR');

	protected rendered = computed<RenderedFilter[]>(() =>
		this.filters().map((filter, index) => {
			const type = filter.type ?? '';
			const dynamic = !!filter.data?.dynamic;
			const schema = FILTER_SCHEMA[type] ?? [];
			const fields: RenderedField[] = schema.flatMap((def) => {
				if (dynamic && def.dynamicPair) {
					return [
						{
							label: `Start ${def.label}`,
							dataKey: def.dynamicPair.startKey,
							kind: def.kind,
							step: def.step,
							tooltip: def.tooltip,
						},
						{
							label: `End ${def.label}`,
							dataKey: def.dynamicPair.endKey,
							kind: def.kind,
							step: def.step,
							tooltip: def.tooltip,
						},
					];
				}
				return [{ label: def.label, dataKey: def.key, kind: def.kind, step: def.step, tooltip: def.tooltip }];
			});
			return { index, filter, type, dynamic, fields };
		})
	);

	protected addFilter(): void {
		// Seeded, not empty: a Color Overlay with no `data.color` throws inside the
		// player's startup preparation and takes the whole project down (DEV-757).
		const next: ContentFilter = { type: this.newType(), data: defaultFilterData(this.newType()) };
		this.filtersChange.emit([...this.filters(), next]);
	}

	protected removeFilter(index: number): void {
		const clone = [...this.filters()];
		clone.splice(index, 1);
		this.filtersChange.emit(clone);
	}

	protected moveFilter(index: number, dir: -1 | 1): void {
		const clone = [...this.filters()];
		const j = index + dir;
		if (j < 0 || j >= clone.length) return;
		[clone[index], clone[j]] = [clone[j], clone[index]];
		this.filtersChange.emit(clone);
	}

	protected setType(index: number, value: string): void {
		// Switching type keeps the previous type's data — which is how a Color
		// Overlay could end up with no colour on an existing filter. Top up the
		// new type's required fields without discarding what the operator set.
		this.patch(index, (f) => {
			f.type = value;
			f.data = normalizeFilter(f).data;
		});
	}

	protected setDynamic(index: number, dynamic: boolean): void {
		this.patch(index, (f) => {
			if (!f.data) f.data = {};
			if (dynamic) {
				f.data.dynamic = true;
			} else {
				delete f.data.dynamic;
				delete f.data.startFrame;
				delete f.data.endFrame;
				delete f.data.startStrength;
				delete f.data.endStrength;
				delete f.data.startAlpha;
				delete f.data.endAlpha;
			}
		});
	}

	protected setFrame(index: number, key: 'startFrame' | 'endFrame', value: number | null): void {
		this.patch(index, (f) => {
			if (!f.data) f.data = {};
			if (value == null) delete f.data[key];
			else f.data[key] = value;
		});
	}

	protected setNumberField(index: number, key: keyof ContentFilterData, value: number | null): void {
		this.patch(index, (f) => {
			if (!f.data) f.data = {};
			if (value != null) {
				(f.data as Record<string, unknown>)[key as string] = value;
			} else if (isRequiredFilterField(f.type, key)) {
				// Same reasoning as setColorField: a required parameter never
				// becomes absent, it returns to its default.
				(f.data as Record<string, unknown>)[key as string] = defaultFilterField(f.type, key);
			} else {
				delete f.data[key];
			}
		});
	}

	protected setColorField(index: number, key: keyof ContentFilterData, value: string): void {
		this.patch(index, (f) => {
			if (!f.data) f.data = {};
			if (value) {
				(f.data as Record<string, unknown>)[key as string] = value;
			} else if (isRequiredFilterField(f.type, key)) {
				// Clearing a required colour used to delete the key and re-create
				// the DEV-757 crash on a filter that was already working. Fall back
				// to the default instead.
				(f.data as Record<string, unknown>)[key as string] = defaultFilterField(f.type, key);
			} else {
				delete f.data[key];
			}
		});
	}

	protected colorToHex(value: unknown): string {
		const s = typeof value === 'string' ? value : '#000000';
		return s.replace('#', '');
	}

	protected hexToColor(hex: string): string {
		return hex.startsWith('#') ? hex : `#${hex}`;
	}

	protected dataNumber(filter: ContentFilter, key: keyof ContentFilterData): number | null {
		const v = filter.data?.[key];
		return typeof v === 'number' ? v : null;
	}

	protected dataString(filter: ContentFilter, key: keyof ContentFilterData): string {
		const v = filter.data?.[key];
		return typeof v === 'string' ? v : '';
	}

	private patch(index: number, mutator: (filter: ContentFilter) => void): void {
		const clone = cloneDeep(this.filters());
		const target = clone[index];
		if (!target) return;
		mutator(target);
		this.filtersChange.emit(clone);
	}
}
